import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  ComposedChart,
} from 'recharts';
import { Card, CardContent } from '../components/ui/Card';
import { KpiCard } from '../components/ui/KpiCard';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { PeriodSelector } from '../components/ui/PeriodSelector';
import { formatBRL } from '../utils/FormatDecimal';
import {
  getTaxes,
  getTaxHistory,
  TaxesResponse,
  TaxHistoryEntry,
} from '../services/apiService';

const REGIME_LABELS: Record<string, string> = {
  'simples-nacional': 'Simples Nacional',
  'lucro-presumido': 'Lucro Presumido',
};

const TRIBUTO_ORDER = ['IRPJ', 'CSLL', 'COFINS', 'PIS', 'CPP', 'ISS'];

export default function TaxesPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [taxes, setTaxes] = useState<TaxesResponse | null>(null);
  const [history, setHistory] = useState<TaxHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const period = `${year}-${String(month).padStart(2, '0')}`;
    Promise.all([getTaxes(period), getTaxHistory({ year, month })])
      .then(([taxData, historyData]) => {
        if (!cancelled) { setTaxes(taxData); setHistory(historyData.months); }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erro ao carregar impostos');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year, month]);

  if (loading && !taxes) return <LoadingSpinner message="Carregando impostos..." />;
  if (error) return <EmptyState title="Erro" description={error} />;
  if (!taxes) return <EmptyState title="Sem dados" description="Nenhum dado disponível" />;

  const isSimples = taxes.regime === 'simples-nacional';
  const valorTotal = isSimples ? taxes.valor_das : taxes.valor_total;

  // Sort tributos by defined order
  const sortedTributos = TRIBUTO_ORDER
    .filter((t) => t in taxes.tributos)
    .map((t) => ({ name: t, ...taxes.tributos[t] }));

  // Add any tributos not in the predefined order
  Object.keys(taxes.tributos).forEach((t) => {
    if (!TRIBUTO_ORDER.includes(t)) {
      sortedTributos.push({ name: t, ...taxes.tributos[t] });
    }
  });

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PeriodSelector
          year={year}
          month={month}
          onChange={(y, m) => { setYear(y); setMonth(m); }}
        />
        <span className="text-sm text-gray-500">
          {REGIME_LABELS[taxes.regime] || taxes.regime}
        </span>
        {loading && <span className="text-xs text-gray-400">Atualizando...</span>}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard
          title="Faturamento"
          value={formatBRL(taxes.faturamento)}
          color="green"
        />
        <KpiCard
          title={isSimples ? 'Valor DAS' : 'Total Impostos'}
          value={formatBRL(valorTotal ?? 0)}
          color="red"
        />
        <KpiCard
          title="Alíquota Efetiva"
          value={`${taxes.aliquota_efetiva.toFixed(2)}%`}
          color="blue"
        />
        {isSimples && taxes.rbt12 != null ? (
          <KpiCard
            title="RBT12"
            value={formatBRL(taxes.rbt12)}
            color="purple"
          />
        ) : (
          <KpiCard
            title="Faixa"
            value={taxes.faixa != null ? `Faixa ${taxes.faixa}` : '—'}
            color="purple"
          />
        )}
      </div>

      {/* Simples Nacional details */}
      {isSimples && taxes.faixa != null && (
        <Card>
          <CardContent>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Detalhamento — Simples Nacional (Anexo III)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
              <div>
                <span className="text-gray-500">RBT12</span>
                <p className="font-semibold">{formatBRL(taxes.rbt12 ?? 0)}</p>
              </div>
              <div>
                <span className="text-gray-500">Faixa</span>
                <p className="font-semibold">{taxes.faixa}</p>
              </div>
              <div>
                <span className="text-gray-500">Alíquota Nominal</span>
                <p className="font-semibold">{taxes.aliquota_nominal?.toFixed(1)}%</p>
              </div>
              <div>
                <span className="text-gray-500">Dedução</span>
                <p className="font-semibold">{formatBRL(taxes.deducao ?? 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tributos table */}
      <Card>
        <CardContent>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Detalhamento por Tributo
          </h3>
          {sortedTributos.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-medium text-gray-500">Tributo</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-500">% s/ alíquota</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-500">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTributos.map(({ name, percentual, valor }) => (
                    <tr key={name} className="border-b border-gray-100 hover:bg-blue-50 even:bg-gray-100">
                      <td className="py-2 px-3 font-medium">{name}</td>
                      <td className="py-2 px-3 text-right text-gray-600">{percentual.toFixed(2)}%</td>
                      <td className="py-2 px-3 text-right font-mono">{formatBRL(valor)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300">
                    <td className="py-2 px-3 font-semibold">Total</td>
                    <td className="py-2 px-3 text-right font-semibold">{taxes.aliquota_efetiva.toFixed(2)}%</td>
                    <td className="py-2 px-3 text-right font-mono font-semibold">
                      {formatBRL(valorTotal ?? 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <EmptyState title="Sem tributos" description="Nenhum imposto calculado para este período" />
          )}
        </CardContent>
      </Card>

      {/* Chart: Tax burden over time */}
      <Card>
        <CardContent>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Carga Tributária / Faturamento (12 meses)
          </h3>
          {history.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v) => `${v.toFixed(0)}%`}
                />
                <Tooltip
                  formatter={(value: any, name: any) =>
                    name === 'percentual'
                      ? `${Number(value).toFixed(2)}%`
                      : formatBRL(Number(value))
                  }
                />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="faturamento"
                  name="Faturamento"
                  fill="#22c55e"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  yAxisId="left"
                  dataKey="carga_tributaria"
                  name="Carga Tributária"
                  fill="#ef4444"
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="percentual"
                  name="% Carga"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState title="Sem dados" description="Nenhum dado para o período" />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
